let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * Toast. Pass `copyText` to add a Copy button (used by the error-copy standard —
 * every user-facing error gets a one-click copyable block). Toasts with a Copy
 * button linger longer so it can actually be clicked.
 */
export function showToast(
  msg: string,
  type: 'ok' | 'err' | 'warn' = 'ok',
  copyText?: string,
): void {
  const el = document.getElementById('toast');
  if (!el) return;
  el.className = `toast-${type}`;
  el.textContent = '';

  const span = document.createElement('span');
  span.textContent = msg;
  el.appendChild(span);

  if (copyText) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Copy';
    btn.style.cssText =
      'margin-left:10px;padding:2px 10px;border:1px solid currentColor;border-radius:6px;' +
      'background:transparent;color:inherit;font:inherit;font-size:12px;font-weight:600;cursor:pointer;flex-shrink:0;';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const done = (): void => { btn.textContent = 'Copied ✓'; };
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(copyText).then(done).catch(() => {});
      } else {
        const ta = document.createElement('textarea');
        ta.value = copyText;
        ta.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); done(); } catch { /* ignore */ }
        ta.remove();
      }
    });
    el.appendChild(btn);
  }

  if (copyText) {
    el.style.display = 'flex';
    el.style.alignItems = 'center';
  } else {
    el.style.display = 'block';
    el.style.removeProperty('align-items');
  }
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { el.style.display = 'none'; }, copyText ? 9000 : 2500);
}

/**
 * Error-copy standard (all apps): show a caught error as a toast with a Copy
 * button that copies `[HUB-ERR hub-people <code> ref:<ref>]` + the message. The
 * server appends `[ref:XXXX]` to the message and logs it under that ref with the
 * source line, so a pasted ref maps straight to the log. Use this in catch
 * blocks instead of `showToast(String(err), 'err')`.
 */
export function errToast(err: unknown): void {
  const e = err as { message?: string; status?: number };
  const raw = e && e.message ? e.message : String(err);
  const code = e && e.status ? String(e.status) : 'ERR';
  const m = /\[ref:([A-Z0-9]+)\]/.exec(raw);
  const refId = m ? m[1] : '';
  const clean = raw.replace(/\s*\[ref:[A-Z0-9]+\]\s*$/, '').trim() || 'Something went wrong';
  // Short, paste-ready. The ref alone maps to the full server log entry
  // (message + source line); with no ref, fall back to the message + code.
  const copy = refId ? `error ref ${refId}` : `error ${code}: ${clean}`;
  showToast(refId ? `${clean}  ·  ref ${refId}` : clean, 'err', copy);
}
